/**
 * CR429: Unified "Why Hidden" diagnostics evaluator.
 *
 * Deterministically evaluates per-section visibility and produces
 * machine-readable reason codes + human-readable explanations.
 */

import { SECTION_REQUIREMENTS, hasField } from "./sectionRequirements";

// ================================================================
// TYPES
// ================================================================

export type SectionId = string;

export interface SectionDiagnostic {
  sectionId: SectionId;
  visible: boolean;
  reason_codes: string[];
  reasons: string[];
  missing_fields: string[];
  notes: string[];
  mappedBlocks?: string[];
}

export interface DealCardDiagnosticsResult {
  environment: string;
  dealType: string | null;
  dealId: string | null;
  hasDeal: boolean;
  section_order: string[];
  diagnostics: Record<SectionId, SectionDiagnostic>;
  summary: {
    visible_count: number;
    hidden_count: number;
    hidden_sections: SectionDiagnostic[];
  };
}

export interface RenderMetaForDiagnostics {
  missing_fields?: string[];
  computed_value_source?: string;
  found?: boolean;
  dedupe_key_present?: boolean;
}

export interface DiagnosticsInput {
  environment: string;
  dealType: string | null;
  dealId: string | null;

  /** Ordered list of section IDs from the resolved config */
  sectionOrder: string[];

  /** Toggle state per section. Supports boolean or { enabled?: boolean } */
  sectionsEnabled: Record<string, boolean | { enabled?: boolean } | null> | null;

  /** Deal payload from CR427/CR428 render payload */
  dealJson: Record<string, unknown> | null;

  /** Meta from CR427 render payload */
  renderMeta: RenderMetaForDiagnostics | null;

  /** Map of section IDs to the block IDs they resolve to */
  sectionToBlockMap: Record<string, string[]>;
}

// ================================================================
// EVALUATOR
// ================================================================

function isSectionEnabled(
  sectionsEnabled: DiagnosticsInput["sectionsEnabled"],
  sectionId: string,
): boolean | null {
  if (!sectionsEnabled) return null; // no toggle data → assume enabled
  const val = sectionsEnabled[sectionId];
  if (val === undefined || val === null) return null;
  if (typeof val === "boolean") return val;
  if (typeof val === "object" && "enabled" in val) {
    return val.enabled ?? null;
  }
  return null;
}

export function computeDealCardVisibilityDiagnostics(
  args: DiagnosticsInput,
): DealCardDiagnosticsResult {
  const {
    environment,
    dealType,
    dealId,
    sectionOrder,
    sectionsEnabled,
    dealJson,
    renderMeta,
    sectionToBlockMap,
  } = args;

  const hasDeal = renderMeta?.found !== false && dealJson != null;
  const diagnostics: Record<SectionId, SectionDiagnostic> = {};

  for (const sectionId of sectionOrder) {
    const diag: SectionDiagnostic = {
      sectionId,
      visible: true,
      reason_codes: [],
      reasons: [],
      missing_fields: [],
      notes: [],
      mappedBlocks: sectionToBlockMap[sectionId] ?? [],
    };

    // ---- 1. Config toggle check ----
    const enabled = isSectionEnabled(sectionsEnabled, sectionId);
    if (enabled === false) {
      diag.visible = false;
      diag.reason_codes.push("TOGGLE_OFF");
      diag.reasons.push("Section toggle is off in config");
    }

    // ---- 2. Block mapping check ----
    const mapped = sectionToBlockMap[sectionId];
    if (!mapped || mapped.length === 0) {
      diag.visible = false;
      diag.reason_codes.push("NO_BLOCK_MAPPING");
      diag.reasons.push("No V2 block mapping exists for this section");
    }

    // ---- 3. Deal payload check ----
    if (!hasDeal) {
      diag.visible = false;
      diag.reason_codes.push("NO_DEAL_PAYLOAD");
      diag.reasons.push(
        renderMeta?.found === false
          ? "Deal not found in database"
          : "No deal payload available",
      );
    }

    // ---- 4. Required fields check (only if we have a deal) ----
    if (hasDeal && dealJson) {
      const req = SECTION_REQUIREMENTS[sectionId];
      if (req) {
        let fieldsSatisfied = false;

        if (req.allOf) {
          const allPresent = req.allOf.every((f) => hasField(dealJson, f));
          if (allPresent) fieldsSatisfied = true;
          else {
            const missing = req.allOf.filter((f) => !hasField(dealJson, f));
            diag.missing_fields.push(...missing);
          }
        }

        if (req.anyOf) {
          // Check if ANY group is fully satisfied
          let anyGroupSatisfied = false;
          const allMissing: string[] = [];

          for (const group of req.anyOf) {
            const groupPresent = group.every((f) => hasField(dealJson, f));
            if (groupPresent) {
              anyGroupSatisfied = true;
              break;
            }
            // Track missing from this group
            const groupMissing = group.filter((f) => !hasField(dealJson, f));
            allMissing.push(...groupMissing);
          }

          if (anyGroupSatisfied) {
            fieldsSatisfied = true;
          } else {
            // Dedupe missing fields
            const unique = [...new Set(allMissing)];
            diag.missing_fields.push(...unique);
          }
        }

        if (!req.allOf && !req.anyOf) {
          fieldsSatisfied = true;
        }

        if (!fieldsSatisfied && diag.missing_fields.length > 0) {
          diag.visible = false;
          diag.reason_codes.push("MISSING_REQUIRED_FIELDS");
          diag.reasons.push(
            `Missing required fields: ${diag.missing_fields.join(", ")}`,
          );
          if (req.note) diag.notes.push(req.note);
        }
      }

      // ---- 5. Cross-reference CR427 meta missing_fields ----
      if (renderMeta?.missing_fields && renderMeta.missing_fields.length > 0) {
        for (const metaField of renderMeta.missing_fields) {
          // Check if this meta missing field is relevant to this section
          const req2 = SECTION_REQUIREMENTS[sectionId];
          if (!req2) continue;
          const allPaths = [
            ...(req2.allOf ?? []),
            ...(req2.anyOf ?? []).flat(),
          ];
          const isRelevant = allPaths.some(
            (p) => p.includes(metaField) || metaField.includes(p.split(".").pop() ?? ""),
          );
          if (isRelevant) {
            diag.notes.push(
              `CR427 meta reports missing: ${metaField}`,
            );
          }
        }
      }
    }

    diagnostics[sectionId] = diag;
  }

  // ---- Summary ----
  const allDiags = Object.values(diagnostics);
  const hiddenSections = allDiags.filter((d) => !d.visible);

  return {
    environment,
    dealType,
    dealId,
    hasDeal,
    section_order: sectionOrder,
    diagnostics,
    summary: {
      visible_count: allDiags.length - hiddenSections.length,
      hidden_count: hiddenSections.length,
      hidden_sections: hiddenSections,
    },
  };
}
